import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { syncAll, getToken, saveUserData, getUserData } from './lib/authSync';
import { loadCachedImages, saveCachedImages, clearCachedImages } from './lib/imageCache';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Canvas from './components/Canvas';
import ToolStrip from './components/ToolStrip';
import CharacterPanelSlim from './components/CharacterPanelSlim';
import BattleModal from './components/BattleModal';
import CharacterUnlockModal from './components/CharacterUnlockModal';
import NameInputModal from './components/NameInputModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { ScrollArea } from './components/ui/scroll-area';
import { Trash2, RotateCcw } from 'lucide-react';
// Example config or API setup file
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:10000';
export const fetchNotes = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`);
  return response.json();
};
function App() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const ALL_ITEMS_FOLDER_ID = '__ALL_ITEMS__';
  const [stickers, setStickers] = useState([]);
  const [noteStickers, setNoteStickers] = useState([]);
  const [images, setImages] = useState([]);
  const [tables, setTables] = useState([]);
  const [todos, setTodos] = useState([]);
  const [sources, setSources] = useState([]);
  const [connections, setConnections] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [unlockedCharacter, setUnlockedCharacter] = useState(null);
  const [stats, setStats] = useState({
    totalNotes: 0,
    totalWords: 0,
    xp: 0,
    level: 1,
    timeSpent: 0,
    battles: 0,
    wins: 0
  });
  const [showNameInput, setShowNameInput] = useState(false);
  const [userName, setUserName] = useState('Adventurer');
  const [showBattle, setShowBattle] = useState(false);
  const [characterPanelOpen, setCharacterPanelOpen] = useState(false);
  const [drawingTool, setDrawingTool] = useState(null); // 'freehand' | 'line' | 'arrow' | 'ellipse' | null
  const [isPremium, setIsPremium] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [drawings, setDrawings] = useState([]);
  const timeIntervalRef = useRef(null);
  const syncTimerRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const syncDirtyRef = useRef(false);
  const lastSyncAtRef = useRef(0);
  const userDataSaveTimerRef = useRef(null);
  const userDataSaveInFlightRef = useRef(false);
  const userDataDirtyRef = useRef(false);
  const lastUserDataSaveAtRef = useRef(0);
  const lastSessionUserIdRef = useRef(localStorage.getItem('userId'));
  const [syncUiState, setSyncUiState] = useState('idle'); // idle | syncing | synced | error
  const [syncUiText, setSyncUiText] = useState('');
  const syncUiTimerRef = useRef(null);
  const imagesDirtyRef = useRef(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [trash, setTrash] = useState([]);
  const isDrawingMode = !!drawingTool;
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [readerModeNoteId, setReaderModeNoteId] = useState(null);
  const [showFolderView, setShowFolderView] = useState(false);
  const safePosition = (pos) =>
  pos && typeof pos.x === 'number' && typeof pos.y === 'number'
    ? pos
    : { x: 100, y: 100 };

  const safeSize = (size, fallback) =>
  size && typeof size.width === 'number' && typeof size.height === 'number'
    ? size
    : fallback;


  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);
  const historyIndexRef = useRef(-1);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  const safeSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorage setItem failed for', key, e?.name || e);
      return false;
    }
  };

  const showSyncing = useCallback((text = 'Syncing...') => {
    if (syncUiTimerRef.current) clearTimeout(syncUiTimerRef.current);
    setSyncUiState('syncing');
    setSyncUiText(text);
  }, []);

  const showSynced = useCallback(() => {
    if (syncUiTimerRef.current) clearTimeout(syncUiTimerRef.current);
    setSyncUiState('synced');
    setSyncUiText('All changes synced');
    syncUiTimerRef.current = setTimeout(() => {
      setSyncUiState('idle');
      setSyncUiText('');
    }, 2000);
  }, []);

  const showSyncError = useCallback((text = 'Sync failed (will retry)') => {
    if (syncUiTimerRef.current) clearTimeout(syncUiTimerRef.current);
    setSyncUiState('error');
    setSyncUiText(text);
    syncUiTimerRef.current = setTimeout(() => {
      setSyncUiState('idle');
      setSyncUiText('');
    }, 4000);
  }, []);


  // Load data from localStorage on mount
useEffect(() => {
  console.log('Loading data from localStorage...');
  try {
    const load = (key) => {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : [];
    };

    const token = getToken();

    const notesKey = token && localStorage.getItem('userNotes') ? 'userNotes' : 'anotequest_notes';

    setNotes(
      load(notesKey).map((n) => ({
        ...n,
        position: safePosition(n.position),
      }))
    );

    setFolders(load('anotequest_folders'));

    setStickers(
      load('anotequest_stickers').map(s => ({
        ...s,
        position: safePosition(s.position),
        rotation: s.rotation ?? 0
      }))
    );

    setNoteStickers(
      load('anotequest_note_stickers').map(s => ({
        ...s,
        position: safePosition(s.position),
        size: safeSize(s.size, { width: 200, height: 160 }),
        paths: s.paths || [],
      }))
    );

    setImages(
      load('anotequest_images').map(i => ({
        ...i,
        position: safePosition(i.position),
        size: safeSize(i.size, { width: 300, height: 200 })
      }))
    );

    setSources(
      load('anotequest_sources').map(s => ({
        ...s,
        position: safePosition(s.position),
        size: safeSize(s.size, { width: 260, height: 120 }),
      }))
    );

    setTables(
      load('anotequest_tables').map(t => ({
        ...t,
        position: safePosition(t.position),
        size: safeSize(t.size, { width: 400, height: 200 }),
        data: t.data ?? [['']]
      }))
    );

    setTodos(
      load('anotequest_todos').map(t => ({
        ...t,
        position: safePosition(t.position)
      }))
    );

    setDrawings(load('anotequest_drawings'));

    setCharacters(load('anotequest_characters'));

    const savedTrash = localStorage.getItem('anotequest_trash');
    if (savedTrash) setTrash(JSON.parse(savedTrash));

    const savedStats = localStorage.getItem('anotequest_stats');
    if (savedStats) setStats(JSON.parse(savedStats));

    const savedPremium = localStorage.getItem('anotequest_premium');
    if (savedPremium) setIsPremium(JSON.parse(savedPremium));

    const savedUserName = localStorage.getItem('anotequest_username');
    if (savedUserName) setUserName(savedUserName);
    else setShowNameInput(true);

    setIsLoaded(true);
  } catch (err) {
    console.error('Corrupted localStorage, resetting', err);
    localStorage.clear();
    setIsLoaded(true);
  }
}, []);

  const hydrateFromServer = useCallback(async () => {
    const shouldHydrateKey = (key, serverValue) => {
      const raw = localStorage.getItem(key);
      if (raw == null) return true;
      if (raw === '[]' && Array.isArray(serverValue) && serverValue.length > 0) return true;
      if (raw === '{}' && serverValue && typeof serverValue === 'object' && Object.keys(serverValue).length > 0) return true;
      return false;
    };
    if (!getToken()) return;
    try {
      const userData = await getUserData();
      if (!userData) return;

      // Only fill from server when localStorage is missing (quota can prevent saving images)
      if (shouldHydrateKey('anotequest_images', userData.images) && Array.isArray(userData.images)) {
        setImages(userData.images.map((i) => ({
          ...i,
          position: safePosition(i.position),
          size: safeSize(i.size, { width: 300, height: 200 }),
        })));
        const userId = localStorage.getItem('userId');
        if (userId) saveCachedImages(userId, userData.images);
      }

      if (shouldHydrateKey('anotequest_todos', userData.todos) && Array.isArray(userData.todos)) {
        setTodos(userData.todos.map((t) => ({ ...t, position: safePosition(t.position) })));
      }

      if (shouldHydrateKey('anotequest_note_stickers', userData.note_stickers) && Array.isArray(userData.note_stickers)) {
        setNoteStickers(userData.note_stickers.map((ns) => ({
          ...ns,
          position: safePosition(ns.position),
          size: safeSize(ns.size, { width: 200, height: 160 }),
          paths: ns.paths || [],
        })));
      }

      if (shouldHydrateKey('anotequest_sources', userData.sources) && Array.isArray(userData.sources)) {
        setSources(userData.sources.map((src) => ({
          ...src,
          position: safePosition(src.position),
          size: safeSize(src.size, { width: 260, height: 120 }),
        })));
      }

      if (shouldHydrateKey('anotequest_stickers', userData.stickers) && Array.isArray(userData.stickers)) {
        setStickers(userData.stickers.map((st) => ({
          ...st,
          position: safePosition(st.position),
        })));
      }

      if (shouldHydrateKey('anotequest_tables', userData.tables) && Array.isArray(userData.tables)) {
        setTables(userData.tables.map((t) => ({
          ...t,
          position: safePosition(t.position),
          size: safeSize(t.size, { width: 400, height: 200 }),
          data: t.data ?? [['']],
        })));
      }

      if (shouldHydrateKey('anotequest_drawings', userData.drawings) && Array.isArray(userData.drawings)) {
        setDrawings(userData.drawings);
      }

      if (shouldHydrateKey('anotequest_trash', userData.trash) && Array.isArray(userData.trash)) {
        setTrash(userData.trash);
      }

      if (shouldHydrateKey('anotequest_folders', userData.folders) && Array.isArray(userData.folders)) {
        setFolders(userData.folders);
      }

      if (shouldHydrateKey('anotequest_characters', userData.characters) && Array.isArray(userData.characters)) {
        setCharacters(userData.characters);
      }

      if (shouldHydrateKey('anotequest_stats', userData.stats) && userData.stats) {
        setStats(userData.stats);
      }

      if (shouldHydrateKey('anotequest_username', userData.username) && typeof userData.username === 'string') {
        setUserName(userData.username);
      }
    } catch (e) {
      console.warn('Failed to hydrate from server:', e?.message || e);
    }
  }, [setImages, setTodos, setNoteStickers, setSources, setFolders, setCharacters, setStats, setUserName]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    hydrateFromServer();
  }, [isLoaded, hydrateFromServer]);

  // Warm image cache from IndexedDB for faster reloads (localStorage can't hold base64 images)
  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;

    const userId = localStorage.getItem('userId');
    if (!userId) return;

    (async () => {
      const cached = await loadCachedImages(userId);
      if (cached && cached.length) {
        setImages(
          cached.map((i) => ({
            ...i,
            position: safePosition(i.position),
            size: safeSize(i.size, { width: 300, height: 200 }),
          }))
        );
      }
    })();
  }, [isLoaded]);

  // Persist images to IndexedDB whenever they change (best-effort)
  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    // Don't block UI; just cache
    saveCachedImages(userId, images);
  }, [images, isLoaded]);


  // If user changes (different account), wipe in-memory state so we don't push previous user's data
  useEffect(() => {
    if (!isLoaded) return;
    const currentUserId = localStorage.getItem('userId');
    if (!currentUserId) return;
    if (lastSessionUserIdRef.current && lastSessionUserIdRef.current !== currentUserId) {
      console.warn('Detected user change. Resetting in-memory state to prevent cross-account mixing.');
      setNotes([]);
      setFolders([]);
      setStickers([]);
      setNoteStickers([]);
      setImages([]);
      setTables([]);
      setTodos([]);
      setSources([]);
      setDrawings([]);
      setTrash([]);
      setStats({
        totalNotes: 0,
        totalWords: 0,
        xp: 0,
        level: 1,
        timeSpent: 0,
        battles: 0,
        wins: 0,
      });
    }
    lastSessionUserIdRef.current = currentUserId;
  }, [isLoaded]);



  // Track time spent
  useEffect(() => {
    if (!isLoaded) return;
    
    timeIntervalRef.current = setInterval(() => {
      setStats(prev => ({
        ...prev,
        timeSpent: prev.timeSpent + 1
      }));
    }, 1000);

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, [isLoaded]);

  // Save to localStorage
  useEffect(() => {
    if (!isLoaded) return;
    const timeout = setTimeout(() => {
      safeSet('anotequest_notes', JSON.stringify(notes));
      if (getToken()) safeSet('userNotes', JSON.stringify(notes));
    }, 500);
    return () => clearTimeout(timeout);
  }, [notes, isLoaded]);

  const buildUserDataSnapshot = useCallback((opts = {}) => ({
    username: localStorage.getItem('anotequest_username') || userName,
    stats,
    folders,
    notes,
    images: opts.includeImages ? images : undefined,
    note_stickers: noteStickers,
    todos,
    characters,
    sources,
    stickers,
    tables,
    drawings,
    trash,
    theme: localStorage.getItem('theme') || undefined,
  }), [userName, stats, folders, notes, images, noteStickers, todos, characters, sources, stickers, tables, drawings, trash]);

  const flushUserDataSave = useCallback(async (opts = {}) => {
    if (!getToken()) return false;
    try {
      showSyncing(opts.label || 'Syncing...');
      await saveUserData(buildUserDataSnapshot({ includeImages: !!opts.includeImages }));
      if (opts.includeImages) imagesDirtyRef.current = false;
      showSynced();
      return true;
    } catch (e) {
      console.warn('Flush save failed:', e?.message || e);
      showSyncError();
      return false;
    }
  }, [buildUserDataSnapshot, showSyncing, showSynced, showSyncError]);

  const scheduleUserDataSave = useCallback((reason = 'state-changed') => {
    if (!getToken()) return;
    userDataDirtyRef.current = true;

    if (userDataSaveTimerRef.current) clearTimeout(userDataSaveTimerRef.current);
    userDataSaveTimerRef.current = setTimeout(async () => {
      if (!userDataDirtyRef.current) return;
      if (userDataSaveInFlightRef.current) return;
      if (!getToken()) {
        userDataDirtyRef.current = false;
        return;
      }

      const now = Date.now();
      if (now - lastUserDataSaveAtRef.current < 30_000) {
        scheduleUserDataSave('rate-limit');
        return;
      }

      userDataSaveInFlightRef.current = true;
      try {
        await flushUserDataSave({ includeImages: false, label: 'Saving...' });
        lastUserDataSaveAtRef.current = Date.now();
        userDataDirtyRef.current = false;
      } catch (e) {
        console.warn('UserData save failed:', e?.message || e);
      } finally {
        userDataSaveInFlightRef.current = false;
      }
    }, 8000);
  }, [flushUserDataSave]);

  const scheduleServerSync = useCallback((reason = 'notes-changed') => {
    if (!getToken()) return;
    syncDirtyRef.current = true;

    // Debounce changes; never sync more often than every 30s
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      if (!syncDirtyRef.current) return;
      if (syncInFlightRef.current) return;
      if (!getToken()) {
        syncDirtyRef.current = false;
        return;
      }

      const now = Date.now();
      if (now - lastSyncAtRef.current < 30_000) {
        scheduleServerSync('rate-limit');
        return;
      }

      syncInFlightRef.current = true;
      showSyncing('Syncing...');
      try {
        await saveUserData(buildUserDataSnapshot({ includeImages: false }));
        await syncAll(notes);
        lastSyncAtRef.current = Date.now();
        syncDirtyRef.current = false;
        showSynced();
      } catch (e) {
        // keep dirty so we can retry later
        console.warn('Background sync failed:', e?.message || e);
        showSyncError();
      } finally {
        syncInFlightRef.current = false;
      }
    }, 5000);
  }, [notes, buildUserDataSnapshot, showSyncing, showSynced, showSyncError]);

  // Schedule background sync when notes change (no constant polling)
  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    scheduleServerSync();
    return () => {};
  }, [notes, isLoaded, scheduleServerSync]);

  // Schedule background UserData save when state changes (images/todos/folders/etc)
  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;

    const handler = () => {
      try {
        const token = getToken();
        if (!token) return;
        const body = JSON.stringify({ userData: buildUserDataSnapshot({ includeImages: false }) });
        fetch('/api/userdata', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body,
          keepalive: true,
        });
      } catch {
        // ignore
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isLoaded, buildUserDataSnapshot]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    scheduleUserDataSave('state-changed');
  }, [
    isLoaded,
    folders,
    notes,
    images,
    noteStickers,
    todos,
    characters,
    sources,
    stickers,
    tables,
    drawings,
    trash,
    stats,
    scheduleUserDataSave,
  ]);


  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_folders', JSON.stringify(folders));
  }, [folders, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_stickers', JSON.stringify(stickers));
  }, [stickers, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_note_stickers', JSON.stringify(noteStickers));
  }, [noteStickers, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_images', JSON.stringify(images));
  }, [images, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_sources', JSON.stringify(sources));
  }, [sources, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_tables', JSON.stringify(tables));
  }, [tables, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_todos', JSON.stringify(todos));
  }, [todos, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_characters', JSON.stringify(characters));
  }, [characters, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_stats', JSON.stringify(stats));
  }, [stats, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_trash', JSON.stringify(trash));
  }, [trash, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_drawings', JSON.stringify(drawings));
  }, [drawings, isLoaded]);

  // Save history for undo/redo (board-wide, last 10 steps)
  useEffect(() => {
    if (!isLoaded || isUndoRedo.current) {
      isUndoRedo.current = false;
      return;
    }

    const state = {
      notes: [...notes],
      stickers: [...stickers],
      noteStickers: [...noteStickers],
      images: [...images],
      sources: [...sources],
      tables: [...tables],
      todos: [...todos],
      drawings: [...drawings],
    };

    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndexRef.current + 1);
      newHistory.push(state);
      // Keep only last 10 states
      if (newHistory.length > 10) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 9));
  }, [notes, stickers, noteStickers, images, sources, tables, todos, drawings, isLoaded]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedo.current = true;
      const prevState = history[historyIndex - 1];
      setNotes(prevState.notes || []);
      setStickers(prevState.stickers || []);
      setNoteStickers(prevState.noteStickers || []);
      setImages(prevState.images || []);
      setSources(prevState.sources || []);
      setTables(prevState.tables || []);
      setTodos(prevState.todos || []);
      setDrawings(prevState.drawings || []);
      setHistoryIndex(prev => prev - 1);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedo.current = true;
      const nextState = history[historyIndex + 1];
      setNotes(nextState.notes || []);
      setStickers(nextState.stickers || []);
      setNoteStickers(nextState.noteStickers || []);
      setImages(nextState.images || []);
      setSources(nextState.sources || []);
      setTables(nextState.tables || []);
      setTodos(nextState.todos || []);
      setDrawings(nextState.drawings || []);
      setHistoryIndex(prev => prev + 1);
    }
  }, [history, historyIndex]);

  const checkCharacterUnlocks = useCallback((totalNotes, totalWords, xp, timeSpent) => {
    const characterTypes = [
      { id: 1, name: 'Scribe the Wizard', requirement: { notes: 5, words: 50, time: 0 }, emoji: '🧙', type: 'wizard', unlocked: false },
      { id: 2, name: 'Knight Notarius', requirement: { notes: 15, words: 200, time: 60 }, emoji: '⚔️', type: 'soldier', unlocked: false },
      { id: 3, name: 'Inky the Dragon', requirement: { notes: 30, words: 500, time: 180 }, emoji: '🐉', type: 'dragon', unlocked: false },
      { id: 4, name: 'Sage the Owl', requirement: { notes: 50, words: 1000, time: 300 }, emoji: '🦉', type: 'sage', unlocked: false },
      { id: 5, name: 'Phoenix Wordsmith', requirement: { notes: 75, words: 1500, time: 600 }, emoji: '🔥', type: 'phoenix', unlocked: false },
      { id: 6, name: 'Warrior Scribbles', requirement: { notes: 100, words: 2500, time: 900 }, emoji: '🛡️', type: 'warrior', unlocked: false },
      { id: 7, name: 'Dragon Lord Quill', requirement: { notes: 150, words: 4000, time: 1800 }, emoji: '🐲', type: 'dragon_lord', unlocked: false },
    ];

    setCharacters(prev => {
      let hasNewUnlock = false;
      let newlyUnlocked = null;
      
      const updated = characterTypes.map(char => {
        const existing = prev.find(c => c.id === char.id);
        const shouldUnlock = 
          totalNotes >= char.requirement.notes && 
          totalWords >= char.requirement.words &&
          timeSpent >= char.requirement.time;
        
        if (!existing?.unlocked && shouldUnlock) {
          hasNewUnlock = true;
          newlyUnlocked = { ...char, unlocked: true, level: 1, xp: 0, position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 }, caged: false };
          return newlyUnlocked;
        }
        
        if (existing?.unlocked) {
          const charLevel = Math.floor(xp / (char.id * 150)) + 1;
          return { ...existing, level: charLevel, xp };
        }
        
        return existing || char;
      });
      
      if (hasNewUnlock && newlyUnlocked) {
        setTimeout(() => setUnlockedCharacter(newlyUnlocked), 500);
      }
      
      return updated;
    });
  }, []);

  // Calculate stats and check for unlocks
  useEffect(() => {
    if (!isLoaded) return;
    
    const totalNotes = notes.length;
    const totalWords = notes.reduce((sum, note) => {
      const wordCount = note.content?.trim().split(/\s+/).filter(w => w.length > 0).length || 0;
      return sum + wordCount;
    }, 0);

    const xp = (totalNotes * 10) + Math.floor(totalWords / 10);
    const level = Math.floor(xp / 100) + 1;

    setStats(prev => ({
      ...prev,
      totalNotes,
      totalWords,
      xp,
      level
    }));

    checkCharacterUnlocks(totalNotes, totalWords, xp, stats.timeSpent);
  }, [notes, isLoaded, checkCharacterUnlocks, stats.timeSpent]);

  const handleNameSubmit = useCallback((name) => {
    setUserName(name);
    safeSet('anotequest_username', name);
    setShowNameInput(false);
  }, []);

  const addNote = useCallback((note) => {
    if (!isPremium && notes.length >= 100) {
      return { error: 'Free tier limited to 100 notes!' };
    }

    // Place new notes near the last one so they feel predictable, not random
    const lastNote = notes[notes.length - 1];
    const defaultPosition = lastNote?.position
      ? { x: lastNote.position.x + 40, y: lastNote.position.y + 40 }
      : { x: 200, y: 160 };

    const basePosition = note?.position || defaultPosition;

    const newNote = {
      id: Date.now(),
      title: 'New Note',
      content: '',
      ...note,
      position: basePosition,
      createdAt: new Date().toISOString(),
      images: note?.images || [],
      folderId: activeFolder || null
    };
    setNotes(prev => [...prev, newNote]);
    return { success: true, note: newNote };
  }, [isPremium, notes, activeFolder]);

  const updateNote = useCallback((id, updates) => {
    setNotes(prev => prev.map(note => 
      note.id === id ? { ...note, ...updates } : note
    ));
  }, []);

  const addToTrash = useCallback((type, item) => {
    setTrash(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        type,
        item,
        deletedAt: new Date().toISOString(),
      },
    ]);
  }, []);

  const deleteNote = useCallback((id) => {
    setNotes(prev => {
      const note = prev.find(n => n.id === id);
      if (note) addToTrash('note', note);
      return prev.filter(n => n.id !== id);
    });
  }, [addToTrash]);

  const addFolder = useCallback((name) => {
    const newFolder = {
      id: Date.now(),
      name,
      createdAt: new Date().toISOString()
    };
    setFolders(prev => [...prev, newFolder]);
  }, []);

  const deleteFolder = useCallback((folderId) => {
    // Remove the folder itself
    setFolders(prev => prev.filter(f => f.id !== folderId));

    // Move items in that folder back to root (no folder)
    setNotes(prev => prev.map(n => n.folderId === folderId ? { ...n, folderId: null } : n));
    setStickers(prev => prev.map(s => s.folderId === folderId ? { ...s, folderId: null } : s));
    setImages(prev => prev.map(i => i.folderId === folderId ? { ...i, folderId: null } : i));
    setSources(prev => prev.map(s => s.folderId === folderId ? { ...s, folderId: null } : s));
    setTables(prev => prev.map(t => t.folderId === folderId ? { ...t, folderId: null } : t));
    setTodos(prev => prev.map(t => t.folderId === folderId ? { ...t, folderId: null } : t));
    setConnections(prev => prev.map(c => c.folderId === folderId ? { ...c, folderId: null } : c));

    // If the deleted folder was active, go back to root
    setActiveFolder(prev => (prev === folderId ? null : prev));
  }, []);

  const addSticker = useCallback((sticker) => {
    const newSticker = {
      id: Date.now() + Math.random(),
      ...sticker,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setStickers(prev => [...prev, newSticker]);
  }, [activeFolder]);

  // Note sticker handlers (yellow freeform-style sticky that you can draw on)
  const addNoteSticker = useCallback((sticker = {}) => {
    const basePosition = sticker.position || { x: 220, y: 200 };
    const newSticker = {
      id: Date.now() + Math.random(),
      position: basePosition,
      size: sticker.size || { width: 200, height: 160 },
      paths: [],
      folderId: activeFolder,
      createdAt: new Date().toISOString(),
    };
    setNoteStickers(prev => [...prev, newSticker]);
    return newSticker;
  }, [activeFolder]);

  const updateNoteSticker = useCallback((id, updates) => {
    setNoteStickers(prev => prev.map(s => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  const deleteNoteSticker = useCallback((id) => {
    setNoteStickers(prev => prev.filter(s => s.id !== id));
  }, []);

  const updateSticker = useCallback((id, updates) => {
    setStickers(prev => prev.map(sticker => 
      sticker.id === id ? { ...sticker, ...updates } : sticker
    ));
  }, []);

  const deleteSticker = useCallback((id) => {
    setStickers(prev => {
      const sticker = prev.find(s => s.id === id);
      if (sticker) addToTrash('sticker', sticker);
      return prev.filter(s => s.id !== id);
    });
  }, [addToTrash]);

  // Image handlers
  const addImage = useCallback((image) => {
    const newImage = {
      id: Date.now() + Math.random(),
      ...image,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    imagesDirtyRef.current = true;
    setImages(prev => [...prev, newImage]);
  }, [activeFolder]);

  const updateImage = useCallback((id, updates) => {
    imagesDirtyRef.current = true;
    setImages(prev => prev.map(img => 
      img.id === id ? { ...img, ...updates } : img
    ));
  }, []);

  const deleteImage = useCallback((id) => {
    imagesDirtyRef.current = true;
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) addToTrash('image', img);
      return prev.filter(i => i.id !== id);
    });
  }, [addToTrash]);

  // Table handlers
  const addTable = useCallback((table) => {
    const newTable = {
      id: Date.now() + Math.random(),
      ...table,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setTables(prev => [...prev, newTable]);
  }, [activeFolder]);

  const updateTable = useCallback((id, updates) => {
    setTables(prev => prev.map(t => 
      t.id === id ? { ...t, ...updates } : t
    ));
  }, []);

  const deleteTable = useCallback((id) => {
    setTables(prev => {
      const tbl = prev.find(t => t.id === id);
      if (tbl) addToTrash('table', tbl);
      return prev.filter(t => t.id !== id);
    });
  }, [addToTrash]);

  // Source / Link handlers
  const addSource = useCallback((source) => {
    if (!source?.url) return;

    const basePosition = source.position || { x: 220, y: 200 };

    const newSource = {
      id: Date.now() + Math.random(),
      url: source.url,
      title: source.title || source.url,
      description: source.description || '',
      position: basePosition,
      size: source.size || { width: 260, height: 120 },
      folderId: activeFolder,
      createdAt: new Date().toISOString(),
    };
    setSources(prev => [...prev, newSource]);
  }, [activeFolder]);

  const updateSource = useCallback((id, updates) => {
    setSources(prev => prev.map(s => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  const deleteSource = useCallback((id) => {
    setSources(prev => {
      const src = prev.find(s => s.id === id);
      if (src) addToTrash('source', src);
      return prev.filter(s => s.id !== id);
    });
  }, [addToTrash]);

  // Todo handlers
  const addTodo = useCallback((todo) => {
    const newTodo = {
      id: Date.now() + Math.random(),
      ...todo,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setTodos(prev => [...prev, newTodo]);
  }, [activeFolder]);

  const updateTodo = useCallback((id, updates) => {
    setTodos(prev => prev.map(t => 
      t.id === id ? { ...t, ...updates } : t
    ));
  }, []);

  const deleteTodo = useCallback((id) => {
    setTodos(prev => {
      const todoItem = prev.find(t => t.id === id);
      if (todoItem) addToTrash('todo', todoItem);
      return prev.filter(t => t.id !== id);
    });
  }, [addToTrash]);

  const restoreTrashItem = useCallback((trashId) => {
    setTrash(prev => {
      const entry = prev.find(t => t.id === trashId);
      if (!entry) return prev;
      const remaining = prev.filter(t => t.id !== trashId);
      const { type, item } = entry;

      switch (type) {
        case 'note':
          setNotes(prevNotes => [...prevNotes, item]);
          break;
        case 'sticker':
          setStickers(prevStickers => [...prevStickers, item]);
          break;
        case 'image':
          setImages(prevImages => [...prevImages, item]);
          break;
        case 'table':
          setTables(prevTables => [...prevTables, item]);
          break;
        case 'todo':
          setTodos(prevTodos => [...prevTodos, item]);
          break;
        case 'source':
          setSources(prevSources => [...prevSources, item]);
          break;
        default:
          break;
      }

      return remaining;
    });
  }, []);

  const deleteTrashItemForever = useCallback((trashId) => {
    setTrash(prev => prev.filter(t => t.id !== trashId));
  }, []);

  // Connection handlers
  const addConnection = useCallback((connection) => {
    const newConnection = {
      ...connection,
      id: Date.now() + Math.random(),
      folderId: activeFolder
    };
    setConnections(prev => [...prev, newConnection]);
  }, [activeFolder]);

  const deleteConnection = useCallback((index) => {
    setConnections(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateCharacter = useCallback((id, updates) => {
    setCharacters(prev => prev.map(char => 
      char.id === id ? { ...char, ...updates } : char
    ));
  }, []);

  const handleBattleWin = useCallback(() => {
    const newWins = stats.wins + 1;
    setStats(prev => ({
      ...prev,
      battles: prev.battles + 1,
      wins: newWins
    }));
    
    if (newWins === 100) {
      setCharacters(prev => {
        const dragonChar = prev.find(c => c.type === 'dragon' || c.type === 'dragon_lord');
        if (dragonChar && !dragonChar.unlocked) {
          setTimeout(() => setUnlockedCharacter({ ...dragonChar, unlocked: true }), 500);
          return prev.map(c => 
            c.id === dragonChar.id ? { ...c, unlocked: true, level: 1, xp: 0, position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 }, caged: false } : c
          );
        }
        return prev;
      });
    }
  }, [stats.wins]);

  const handleBattleLose = useCallback(() => {
    setStats(prev => ({
      ...prev,
      battles: prev.battles + 1
    }));
  }, []);

  // Filter notes by folder and search
  const filteredNotes = useMemo(() => {
    let result = activeFolder === ALL_ITEMS_FOLDER_ID
      ? notes
      : activeFolder != null
        ? notes.filter((note) => note.folderId === activeFolder)
        : notes.filter((note) => !note.folderId);
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(note => 
        note.title?.toLowerCase().includes(query) ||
        note.content?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [notes, activeFolder, searchQuery]);

  // Filter other items by folder (each folder has its own canvas)
  const filteredStickers = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return stickers;
    return activeFolder != null
      ? stickers.filter((st) => st.folderId === activeFolder)
      : stickers.filter((st) => !st.folderId);
  }, [stickers, activeFolder]);

  const filteredImages = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return images;
    return activeFolder != null
      ? images.filter((i) => i.folderId === activeFolder)
      : images.filter((i) => !i.folderId);
  }, [images, activeFolder]);

  const filteredTables = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return tables;
    return activeFolder != null
      ? tables.filter((t) => t.folderId === activeFolder)
      : tables.filter((t) => !t.folderId);
  }, [tables, activeFolder]);

  const filteredSources = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return sources;
    return activeFolder != null
      ? sources.filter((src) => src.folderId === activeFolder)
      : sources.filter((src) => !src.folderId);
  }, [sources, activeFolder]);

  const filteredTodos = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return todos;
    return activeFolder != null
      ? todos.filter((t) => t.folderId === activeFolder)
      : todos.filter((t) => !t.folderId);
  }, [todos, activeFolder]);


  const handleLogout = useCallback(async () => {
    try {
      if (getToken()) {
        toast.loading('Syncing...');
        showSyncing('Final sync...');
        await flushUserDataSave({ includeImages: imagesDirtyRef.current, label: 'Final sync...' });
        await syncAll(notes);
        toast.dismiss();
      }
    } catch (e) {
      toast.dismiss();
      toast.error(e?.message || 'Final sync failed');
    } finally {
      localStorage.clear();
      navigate('/login', { replace: true });
    }
  }, [notes, navigate, showSyncing, flushUserDataSave]);


  if (!isLoaded) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading AnoteQuest...</div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-background pt-14">

      <Header
        stats={stats}
        isPremium={isPremium}
        isDrawingMode={isDrawingMode}
        userName={userName}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        notes={notes}
        tables={tables}
        todos={todos}
        folders={folders}
        activeFolder={activeFolder}
        setActiveFolder={setActiveFolder}
        addFolder={addFolder}
        deleteFolder={deleteFolder}
        showFolderView={showFolderView}
        setShowFolderView={setShowFolderView}
      onLogout={handleLogout}
      />
      
      {/* Folder View Modal */}
      {showFolderView && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md pt-14">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-xl font-bold font-display">Your Folders</h2>
              <Button variant="outline" onClick={() => setShowFolderView(false)}>
                Back to Canvas
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {/* All Notes folder */}
                <div
                  onClick={() => {
                    setActiveFolder(null);
                    setShowFolderView(false);
                  }}
                  className={`group cursor-pointer flex flex-col items-center p-4 rounded-xl border-2 transition-all hover:shadow-lg hover:scale-105 ${
                    activeFolder === null 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border bg-card hover:border-primary/50'
                  }`}
                >
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary/20 mb-3">
                    <span className="text-3xl">📝</span>
                  </div>
                  <span className="text-sm font-medium text-center">All Notes</span>
                  <span className="text-xs text-muted-foreground mt-1">{notes.filter(n => !n.folderId).length} notes</span>
                </div>
                
                
                {/* Everything (all folders) */}
                <div
                  onClick={() => {
                    setActiveFolder(ALL_ITEMS_FOLDER_ID);
                    setShowFolderView(false);
                  }}
                  className={`group cursor-pointer flex flex-col items-center p-4 rounded-xl border-2 transition-all hover:shadow-lg hover:scale-105 ${
                    activeFolder === ALL_ITEMS_FOLDER_ID
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-card hover:border-primary/50'
                  }`}
                >
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary/20 mb-3">
                    <span className="text-3xl">🌐</span>
                  </div>
                  <span className="text-sm font-medium text-center">Everything</span>
                  <span className="text-xs text-muted-foreground mt-1">{notes.length} notes</span>
                </div>

                {/* Existing folders */}
                {folders.map(folder => (
                  <div
                    key={folder.id}
                    onClick={() => {
                      setActiveFolder(folder.id);
                      setShowFolderView(false);
                    }}
                    className={`group cursor-pointer flex flex-col items-center p-4 rounded-xl border-2 transition-all hover:shadow-lg hover:scale-105 ${
                      activeFolder === folder.id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-accent/20 mb-3">
                      <span className="text-3xl">📁</span>
                    </div>
                    <span className="text-sm font-medium text-center truncate max-w-full">{folder.name}</span>
                    <span className="text-xs text-muted-foreground mt-1">
                      {notes.filter(n => n.folderId === folder.id).length} notes
                    </span>
                  </div>
                ))}
                
                {/* Add new folder card */}
                <div
                  onClick={() => {
                    const name = prompt('Enter folder name:');
                    if (name && name.trim()) {
                      addFolder(name.trim());
                    }
                  }}
                  className="cursor-pointer flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50 transition-all"
                >
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-muted mb-3">
                    <span className="text-3xl">➕</span>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">New Folder</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 flex overflow-hidden relative">
        {/* Floating Tool Strip */}
        <ToolStrip
          onAddNote={() => addNote()}
          drawingTool={drawingTool}
          setDrawingTool={setDrawingTool}
          addSticker={addSticker}
          onAddSource={addSource}
          addNoteSticker={addNoteSticker}
          folders={folders}
          notes={notes}
          activeFolder={activeFolder}
          setActiveFolder={setActiveFolder}
          addFolder={addFolder}
          deleteFolder={deleteFolder}
          onAddImage={addImage}
          onAddTable={addTable}
          onAddTodo={addTodo}
          activeNote={notes.find(n => n.id === activeNoteId)}
          onClearActiveNote={() => setActiveNoteId(null)}
          updateNote={updateNote}
          deleteNote={deleteNote}
          onOpenReaderMode={(noteId) => setReaderModeNoteId(noteId)}
        />
        
        {/* Main Canvas */}
        <div className="flex-1 relative overflow-hidden">
          <Canvas
            notes={filteredNotes}
            totalNoteCount={notes.length}
            stickers={filteredStickers}
            noteStickers={noteStickers}
            images={filteredImages}
            tables={filteredTables}
            todos={filteredTodos}
            sources={filteredSources}
            drawings={drawings}
            setDrawings={setDrawings}
            drawingTool={drawingTool}
            // characters feature temporarily disabled
            characters={[]}
            addNote={addNote}
            updateNote={updateNote}
            deleteNote={deleteNote}
            addSticker={addSticker}
            updateSticker={updateSticker}
            deleteSticker={deleteSticker}
            updateNoteSticker={updateNoteSticker}
            deleteNoteSticker={deleteNoteSticker}
            addImage={addImage}
            updateImage={updateImage}
            deleteImage={deleteImage}
            addTable={addTable}
            updateTable={updateTable}
            deleteTable={deleteTable}
            addTodo={addTodo}
            updateTodo={updateTodo}
            deleteTodo={deleteTodo}
            updateSource={updateSource}
            deleteSource={deleteSource}
            updateCharacter={updateCharacter}
            folders={folders}
            isPremium={isPremium}
            isDrawingMode={isDrawingMode}
            onCloseDrawing={() => setDrawingTool(null)}
            userName={userName}
            activeFolder={activeFolder}
            onOpenTrash={() => setIsTrashOpen(true)}
            onNoteClick={(note) => setActiveNoteId(note.id)}
            readerModeNoteId={readerModeNoteId}
            onReaderModeClosed={() => setReaderModeNoteId(null)}
          />
        </div>
        
        {/* Slim Character Panel (temporarily disabled) */}
        {false && characterPanelOpen && (
          <CharacterPanelSlim
            characters={characters}
            updateCharacter={updateCharacter}
            onClose={() => setCharacterPanelOpen(false)}
          />
        )}
        {(syncUiState === 'syncing' || syncUiState === 'synced' || syncUiState === 'error') && (
        <div
          data-anotequest-sync-indicator
          className={`fixed bottom-4 right-4 z-50 rounded-md px-3 py-2 text-sm shadow-lg border ${
            syncUiState === 'error'
              ? 'bg-destructive text-destructive-foreground border-destructive/40'
              : syncUiState === 'synced'
                ? 'bg-emerald-600 text-white border-emerald-700/40'
                : 'bg-card text-foreground border-border'
          }`}
        >
          {syncUiText || (syncUiState === 'syncing' ? 'Syncing...' : syncUiState === 'synced' ? 'All changes synced' : 'Sync failed')}
        </div>
      )}

      </div>

      {/* Modals */}
      {showNameInput && (
        <NameInputModal onComplete={handleNameSubmit} />
      )}
      {false && showBattle && (
        <BattleModal 
          onClose={() => setShowBattle(false)}
          onWin={handleBattleWin}
          onLose={handleBattleLose}
          playerStats={stats}
        />
      )}
      {false && unlockedCharacter && (
        <CharacterUnlockModal 
          character={unlockedCharacter}
          onClose={() => setUnlockedCharacter(null)}
        />
      )}

      {/* Trash modal for restoring or permanently deleting items */}
      <Dialog open={isTrashOpen} onOpenChange={setIsTrashOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Trash
            </DialogTitle>
            <DialogDescription>
              Recently removed items. Restore them to the canvas or delete them forever.
            </DialogDescription>
          </DialogHeader>

          {trash.length === 0 ? (
            <p className="text-sm text-muted-foreground">Trash is empty.</p>
          ) : (
            <ScrollArea className="max-h-64 mt-2">
              <div className="space-y-3 pr-2">
                {trash
                  .slice()
                  .reverse()
                  .map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between rounded-md border border-border bg-card/80 px-3 py-2"
                    >
                      <div className="flex flex-col text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">
                            {entry.type}
                          </Badge>
                          <span className="font-medium truncate max-w-[180px]">
                            {entry.type === 'note'
                              ? entry.item.title || 'Untitled note'
                              : entry.type === 'todo'
                              ? entry.item.title || 'Todo list'
                              : entry.type === 'table'
                              ? 'Table'
                              : entry.type === 'image'
                              ? 'Image'
                              : 'Sticker'}
                          </span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          Removed {new Date(entry.deletedAt).toLocaleString()}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => restoreTrashItem(entry.id)}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 px-2 text-xs"
                          onClick={() => deleteTrashItemForever(entry.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
